'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { format } from 'date-fns';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Calendar } from '@eliadmin/core/src/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@eliadmin/core/src/components/ui/popover';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { IoIosArrowDown } from 'react-icons/io';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { AiOutlineExclamationCircle } from 'react-icons/ai';

import { fetchAvailableHousingProperties } from '@eliadmin/core/src/api/housing/housing-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import {
  formatSkDateRange,
  pluralGuests,
} from '@eliadmin/core/src/components/reservation/reservation-form/date-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const calendarClassNames = {
  day_button: 'h-8 w-8 p-0 font-normal text-foreground rounded-md hover:bg-muted cursor-pointer',
  selected:
    'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md',
  outside: 'text-muted-foreground opacity-40',
  disabled: 'text-muted-foreground opacity-30 cursor-not-allowed pointer-events-none',
  today: 'bg-muted font-semibold',
  weekday: 'text-muted-foreground w-8 font-medium text-[0.75rem] text-center',
};

function addNights(date: Date, nights: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + nights);
  return result;
}

function isPeakSeason(date: Date): boolean {
  const month = date.getMonth();
  const day = date.getDate();
  return month === 6 || month === 7 || (month === 11 && day >= 24) || (month === 0 && day <= 3);
}

export function HousingSearch({
  className,
}: {
  className?: { section?: string; container?: string };
}) {
  const { t } = useTranslations();
  const router = useRouter();
  const locale = useLocaleStore(s => s.currentLocale);
  const [arrival, setArrival] = useState<Date | undefined>();
  const [departure, setDeparture] = useState<Date | undefined>();
  const [guests, setGuests] = useState(2);
  const [dateOpen, setDateOpen] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);

  const peakSeason = arrival ? isPeakSeason(arrival) : false;
  const minNights = peakSeason ? 3 : 2;

  const dateLabel =
    arrival && departure ? formatSkDateRange(arrival, departure) : '14. jún - 23. jún 2026';

  const handleVerify = async () => {
    setAvailabilityError(null);

    const params = new URLSearchParams();
    if (!arrival || !departure) {
      params.set('guests', String(guests));
      router.push(`/rezervacia?${params.toString()}`);
      return;
    }

    const dateFrom = format(arrival, 'yyyy-MM-dd');
    const dateTo = format(departure, 'yyyy-MM-dd');

    setIsChecking(true);
    try {
      const { data } = await fetchAvailableHousingProperties({
        locale,
        body: { date_from: dateFrom, date_to: dateTo },
      });

      if (data.length === 0) {
        setAvailabilityError(
          'Pre zvolený termín nie je dostupné žiadne ubytovanie. Skúste prosím iný termín.',
        );
        return;
      }

      params.set('arrival', dateFrom);
      params.set('departure', dateTo);
      params.set('guests', String(guests));
      router.push(`/rezervacia?${params.toString()}`);
    } catch {
      setAvailabilityError('Overenie dostupnosti sa nepodarilo. Skúste to prosím znova.');
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <section className={cn('custom-section', className?.section)}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center rounded-[24px] bg-white p-4 md:flex-row md:justify-between md:rounded-full',
          'ea-housing-search__form-container',
          className?.container,
        )}
      >
        {/* Property selector — static display */}
        <div
          className={cn(
            'relative flex w-full flex-1 items-center justify-start gap-3.5 px-6 py-3 md:px-3 md:py-2',
            'ea-housing-search__input-container',
          )}
        >
          <div className="flex max-h-11 min-h-11 min-w-11 max-w-11 items-center justify-center rounded-full bg-[#F2EEE7]">
            <Image
              src={'/images/icons/search/housing.svg'}
              alt="Ubytovanie"
              width={20}
              height={20}
              className="shrink-0 object-contain"
            />
          </div>
          <div className="flex flex-col items-start justify-start gap-[2px]">
            <p className="text-sm font-normal leading-[100%] text-secondary">Ubytovanie</p>
            <div className="flex items-center justify-start gap-[6px]">
              <p className="text-base font-bold leading-[100%] text-secondary">Chata Horec</p>
              <IoIosArrowDown size={14} className="text-primary" />
            </div>
          </div>
        </div>

        {/* Date range picker */}
        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <button type="button"
              className={cn(
                'relative flex w-full flex-1 cursor-pointer text-left items-center justify-start gap-3.5 px-6 py-3 md:px-6 md:py-2',
                'ea-housing-search__input-container',
              )}
            >
              <span className="flex max-h-11 min-h-11 min-w-11 max-w-11 items-center justify-center rounded-full bg-[#F2EEE7]">
                <Image
                  src={'/images/icons/search/date.svg'}
                  alt={t('text147')}
                  width={20}
                  height={20}
                  className="shrink-0 object-contain"
                />
              </span>
              <span className="flex flex-col items-start justify-start gap-[2px]">
                <span className="text-sm font-normal leading-[100%] text-secondary">
                  Príchod - odchod
                </span>
                <span className="flex items-center justify-start gap-[6px]">
                  <span
                    className={cn(
                      'text-base font-bold leading-[100%]',
                      arrival && departure ? 'text-secondary' : 'text-secondary',
                    )}
                  >
                    {dateLabel}
                  </span>
                  <IoIosArrowDown size={14} className="text-primary" />
                </span>
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-4" align="start">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div>
                <p className="mb-2 text-sm font-semibold text-secondary">Príchod</p>
                <Calendar
                  mode="single"
                  selected={arrival}
                  onSelect={date => {
                    setArrival(date);
                    if (date && departure && departure < addNights(date, minNights)) {
                      setDeparture(undefined);
                    }
                  }}
                  disabled={date => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  classNames={calendarClassNames}
                />
              </div>
              <div>
                <p className="mb-2 text-sm font-semibold text-secondary">Odchod</p>
                <Calendar
                  mode="single"
                  selected={departure}
                  onSelect={date => {
                    setDeparture(date);
                    if (date) setDateOpen(false);
                  }}
                  disabled={date => {
                    const today = new Date(new Date().setHours(0, 0, 0, 0));
                    return arrival ? date < addNights(arrival, minNights) : date < today;
                  }}
                  classNames={calendarClassNames}
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Guest counter */}
        <Popover open={guestsOpen} onOpenChange={setGuestsOpen}>
          <PopoverTrigger asChild>
            <button type="button"
              className={cn(
                'relative flex w-full flex-1 cursor-pointer text-left items-center justify-start gap-3.5 px-6 py-3 md:px-6 md:py-2',
              )}
            >
              <span className="flex max-h-11 min-h-11 min-w-11 max-w-11 items-center justify-center rounded-full bg-[#F2EEE7]">
                <Image
                  src={'/images/icons/search/people.svg'}
                  alt="Hostia"
                  width={20}
                  height={20}
                  className="shrink-0 object-contain"
                />
              </span>
              <span className="flex flex-col items-start justify-start gap-[2px]">
                <span className="text-sm font-normal leading-[100%] text-secondary">Hostia</span>
                <span className="flex items-center justify-start gap-[6px]">
                  <span className="text-base font-bold leading-[100%] text-secondary">
                    {pluralGuests(guests)}
                  </span>
                  <IoIosArrowDown size={14} className="text-primary" />
                </span>
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="start">
            <div className="flex items-center justify-between gap-4">
              <p className="text-base font-medium text-secondary">Počet hostí</p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label="Znížiť počet hostí"
                  onClick={() => setGuests(g => Math.max(1, g - 1))}
                  disabled={guests <= 1}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F2EEE7] text-secondary transition-opacity disabled:opacity-30"
                >
                  <LuMinus size={14} />
                </button>
                <span className="w-6 text-center text-base font-bold text-secondary">{guests}</span>
                <button
                  type="button"
                  aria-label="Zvýšiť počet hostí"
                  onClick={() => setGuests(g => g + 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F2EEE7] text-secondary"
                >
                  <LuPlus size={14} />
                </button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Button
          onClick={handleVerify}
          isLoading={isChecking}
          disabled={isChecking}
          className="h-[50px] w-full whitespace-nowrap sm:h-14 md:h-16 md:w-fit"
        >
          {t('text691')}
        </Button>
      </div>
      {availabilityError && (
        <div
          className={cn(
            'custom-container',
            'mt-3 flex w-full items-center justify-start gap-2 rounded-[24px] bg-[#FFD9CC] px-4 py-3 sm:px-6',
          )}
        >
          <AiOutlineExclamationCircle className="max-h-5 min-h-5 min-w-5 max-w-5 text-secondary" />
          <p className="text-left text-sm font-bold leading-[130%] text-secondary">
            {availabilityError}
          </p>
        </div>
      )}
    </section>
  );
}
